import type { ComponentProps } from 'react';
import { ObservationCapture } from '@/components/widget/observation-capture/observation-capture';
import { useCaptureFlowStore } from '@/store/useCaptureFlowStore';
import { useEvidenceToolStore } from '@/store/useEvidenceToolStore';
import type { ObservationOption } from '@/components/provenance/observation-labels';
import { ChatComposer, COMPOSER_SHELL_CLASS } from './ChatComposer';
import { ChatComposerModelSelect } from './ChatComposerModelSelect';
import { EvidenceToolSwitch } from './EvidenceToolSwitch';

type ChatComposerProps = ComponentProps<typeof ChatComposer> & {
  /** Built from chat history so the selector and the history cards name Observations identically. */
  observationOptions?: readonly ObservationOption[];
  /**
   * The evidence tools gate on this, not on `disabled`. Conversing and capturing are
   * different acts: a streaming turn must stop the member sending a second message, but
   * it must never stop them recording what is in front of them.
   */
  captureDisabled: boolean;
  /** The NeoChat the capture tool is mounted in, so captures file against it. */
  captureOriginProvenanceId?: number;
};

// The evidence-tool frame. Typing an instruction and capturing a photo are the same act,
// so the tools share one slot and exactly one is mounted. A new evidence tool is an entry
// in EvidenceToolRegistry plus a branch here; nothing else in the chat surface changes.
export function Composer({
  observationOptions,
  captureDisabled,
  captureOriginProvenanceId,
  ...props
}: ChatComposerProps) {
  const activeToolId = useEvidenceToolStore((state) => state.activeToolId);
  const captureRequest = useEvidenceToolStore((state) => state.captureRequest);
  const submitCapture = useCaptureFlowStore((state) => state.submitCapture);
  const observationGroupIdByItemId = useCaptureFlowStore(
    (state) => state.observationGroupIdByItemId
  );
  const setSessionObservationGroup = useCaptureFlowStore(
    (state) => state.setSessionObservationGroup
  );

  if (activeToolId === 'observation' && captureRequest) {
    const { sessionId } = captureRequest;
    // The selector reads and writes the same store field observationGroupInput() sends,
    // so choosing an Observation IS the grouping decision — nothing else to keep in sync.
    const selectedObservationGroupId = observationGroupIdByItemId[sessionId] ?? null;
    return (
      <ObservationCapture
        sessionId={sessionId}
        className={COMPOSER_SHELL_CLASS}
        defaultMode={captureRequest.defaultMode}
        linkContentId={captureRequest.linkContentId ?? null}
        originProvenanceContentId={captureOriginProvenanceId}
        observationOptions={observationOptions}
        selectedObservationGroupId={selectedObservationGroupId}
        onSelectObservation={(groupId) => setSessionObservationGroup(sessionId, groupId)}
        disabled={captureDisabled}
        modelSlot={<ChatComposerModelSelect disabled={props.disabled} />}
        switchSlot={<EvidenceToolSwitch current="observation" disabled={props.disabled} />}
        onSubmitted={({ photoIds, recordingIds, videoIds, linkContentId, edgeKind, observationGroupId }) => {
          submitCapture({
            itemId: sessionId,
            photoIds,
            videoIds,
            recordingIds,
            linkContentId,
            edgeKind,
            observationGroupId,
          });
        }}
      />
    );
  }

  return <ChatComposer {...props} switchSlot={<EvidenceToolSwitch current="chat" disabled={props.disabled} />} />;
}
